// components/VATReport.jsx
import React, { useState, useEffect } from 'react';
import vatAPI from '../api/vatAPI';
import { useAuth } from '../context/AuthContext';
import ReportToolbar from '../shared/ReportToolbar';

const VATReport = () => {
  const { selectedClient } = useAuth();
  const accountingClientId = selectedClient?._id || localStorage.getItem('accountingClientId') || null;
  const accountingClientName = selectedClient?.name || '';
  const [vatData, setVatData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fiscalYear, setFiscalYear] = useState(null);
  const [selectedQuarter, setSelectedQuarter] = useState(1);
  const [selectedMonth, setSelectedMonth] = useState(null);

  useEffect(() => {
    if (accountingClientId) {
      const fy = localStorage.getItem(`activeFiscalYear:${accountingClientId}`);
      if (fy) setFiscalYear(parseInt(fy));
    }
  }, [accountingClientId]);

  useEffect(() => {
    const onFYChange = () => {
      if (accountingClientId) {
        const fy = localStorage.getItem(`activeFiscalYear:${accountingClientId}`);
        if (fy) setFiscalYear(parseInt(fy));
      }
    };
    window.addEventListener('fiscalYear:changed', onFYChange);
    return () => window.removeEventListener('fiscalYear:changed', onFYChange);
  }, [accountingClientId]);

  const fetchVATData = async () => {
    if (!accountingClientId || !fiscalYear) {
      setError('Accounting Client and Fiscal Year are required');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const params = {
        fiscalYear: fiscalYear.toString(),
        quarter: selectedQuarter.toString(),
      };
      if (selectedMonth) {
        params.month = selectedMonth.toString();
      }

      const response = await vatAPI.getByClient(accountingClientId, params);
      setVatData(response?.data || []);
    } catch (err) {
      setError(err.message || 'Failed to fetch VAT records');
      console.error('VAT Report Error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (accountingClientId && fiscalYear) {
      fetchVATData();
    }
  }, [accountingClientId, fiscalYear, selectedQuarter, selectedMonth]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'PHP',
    }).format(amount || 0);
  };

  const formatCurrencyForCSV = (amount) => {
    return (amount || 0).toFixed(2);
  };

  const monthNames = ['', 'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  const quarterMonths = {
    1: [1, 2, 3],
    2: [4, 5, 6],
    3: [7, 8, 9],
    4: [10, 11, 12],
  };

  const recordTypeLabels = {
    'sales': 'Sales',
    'purchases': 'Purchases',
    'imports': 'Imports',
    'other': 'Other',
  };

  const computeTotals = () => {
    if (!vatData || vatData.length === 0) {
      return { totalGross: 0, totalVAT: 0, totalNet: 0 };
    }

    return {
      totalGross: vatData.reduce((sum, r) => sum + (r.grossAmount || 0), 0),
      totalVAT: vatData.reduce((sum, r) => sum + (r.vatAmount || 0), 0),
      totalNet: vatData.reduce((sum, r) => sum + (r.netAmount || 0), 0),
    };
  };

  const totals = computeTotals();

  // CSV Export Function
  const exportToCSV = () => {
    if (!vatData || vatData.length === 0) {
      alert('No data to export');
      return;
    }

    const csvData = [
      ['VAT Report', `FY ${fiscalYear}`, `Q${selectedQuarter}`],
      [accountingClientName],
      [],
      ['Month', 'Record Type', 'Gross Amount', 'VAT Amount', 'Net Amount'],
      
      ...vatData.map(item => [
        monthNames[item.month] || item.month,
        recordTypeLabels[item.recordType] || item.recordType,
        formatCurrencyForCSV(item.grossAmount),
        formatCurrencyForCSV(item.vatAmount),
        formatCurrencyForCSV(item.netAmount),
      ]),
      
      [],
      ['TOTALS', '', formatCurrencyForCSV(totals.totalGross), formatCurrencyForCSV(totals.totalVAT), formatCurrencyForCSV(totals.totalNet)],
    ];

    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `vat-report-fy${fiscalYear}-q${selectedQuarter}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Print function
  const handlePrint = () => {
    if (!vatData || vatData.length === 0) {
      alert('No data to print');
      return;
    }

    const rows = vatData.map(item => `
      <tr>
        <td style="padding:4px 8px;border-bottom:1px solid #e5e7eb;">${monthNames[item.month] || item.month}</td>
        <td style="padding:4px 8px;border-bottom:1px solid #e5e7eb;">${recordTypeLabels[item.recordType] || item.recordType}</td>
        <td style="padding:4px 8px;text-align:right;border-bottom:1px solid #e5e7eb;font-family:monospace;">${formatCurrency(item.grossAmount)}</td>
        <td style="padding:4px 8px;text-align:right;border-bottom:1px solid #e5e7eb;font-family:monospace;color:#059669;">${formatCurrency(item.vatAmount)}</td>
        <td style="padding:4px 8px;text-align:right;border-bottom:1px solid #e5e7eb;font-family:monospace;">${formatCurrency(item.netAmount)}</td>
      </tr>
    `).join('');

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>VAT Report</title>
<style>
body{font-family:'Courier New',monospace;font-size:11px;color:#111;margin:32px}
.hdr{text-align:center;margin-bottom:20px}
.hdr h1{font-size:16px;margin:0 0 2px;font-weight:bold}
.hdr .sub{font-size:11px;color:#6b7280;margin-top:4px}
table{width:100%;border-collapse:collapse;margin-top:12px}
th{padding:6px 8px;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:.04em;border-bottom:2px solid #d1d5db;background:#f3f4f6}
th.r{text-align:right}
tr:hover{background:#fafafa}
.tb{text-align:center;margin-bottom:16px}
.tb button{padding:6px 16px;font-size:11px;cursor:pointer;background:#0d9488;color:#fff;border:none;border-radius:4px}
.totals{font-weight:bold;background:#f3f4f6;border-top:2px solid #d1d5db}
@media print{.tb{display:none}@page{margin:15mm}}
</style></head><body>
<div class="tb"><button onclick="window.print()">🖨 Print / Save as PDF</button></div>
<div class="hdr">
<div style="font-size:14px;font-weight:bold;margin-bottom:2px;">${accountingClientName}</div>
<h1>VAT REPORT</h1>
<div class="sub">Fiscal Year ${fiscalYear} | Quarter ${selectedQuarter}</div>
</div>
<table>
<thead><tr>
<th>Month</th><th>Record Type</th>
<th class="r">Gross Amount</th><th class="r">VAT Amount</th><th class="r">Net Amount</th>
</tr></thead>
<tbody>
${rows}
<tr class="totals">
<td colspan="2" style="padding:6px 8px;">TOTALS</td>
<td style="padding:6px 8px;text-align:right;">${formatCurrency(totals.totalGross)}</td>
<td style="padding:6px 8px;text-align:right;">${formatCurrency(totals.totalVAT)}</td>
<td style="padding:6px 8px;text-align:right;">${formatCurrency(totals.totalNet)}</td>
</tr>
</tbody>
</table>
</body></html>`;

    const win = window.open('', '_blank', 'width=1000,height=900');
    win.document.write(html);
    win.document.close();
    setTimeout(() => { win.focus(); win.print(); }, 500);
  };

  return (
    <div className="w-full min-h-screen bg-gray-50 p-8">
      <div className="max-w-5xl mx-auto">
        {/* Header Section */}
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">VAT REPORT</h1>
          <div className="flex justify-center">
            <ReportToolbar
              showPeriod={false}
              onExportCSV={vatData?.length ? exportToCSV : undefined}
              onPrint={vatData?.length ? handlePrint : undefined}
              onRefresh={fetchVATData}
              refreshing={loading}
              extra={
                <>
                  <div className="text-sm text-ink-body">
                    Fiscal year <span className="font-medium text-ink">{fiscalYear || '—'}</span>
                  </div>
                  <select
                    value={selectedQuarter}
                    onChange={(e) => setSelectedQuarter(parseInt(e.target.value))}
                    className="px-3 py-2 border border-ink-line rounded-md text-ink text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  >
                    {[1, 2, 3, 4].map(q => (
                      <option key={q} value={q}>Q{q}</option>
                    ))}
                  </select>
                  <select
                    value={selectedMonth || ''}
                    onChange={(e) => setSelectedMonth(e.target.value ? parseInt(e.target.value) : null)}
                    className="px-3 py-2 border border-ink-line rounded-md text-ink text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">All months</option>
                    {quarterMonths[selectedQuarter].map(m => (
                      <option key={m} value={m}>{monthNames[m]}</option>
                    ))}
                  </select>
                </>
              }
            />
          </div>
          {accountingClientName && (
            <div className="text-sm text-gray-600 font-medium mt-3">
              {accountingClientName}
            </div>
          )}
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 rounded-md bg-red-50 p-4">
            <div className="text-sm text-red-700">{error}</div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
          </div>
        )}

        {/* VAT Report Content */}
        {!loading && vatData && (
          <div className="bg-white p-12 font-mono text-sm max-w-4xl mx-auto">
            {/* Column Headers */}
            <div className="flex justify-between mb-4 font-bold text-gray-900 border-b border-gray-300 pb-2">
              <span className="flex-1">Month</span>
              <span className="flex-1">Record Type</span>
              <span className="w-32 text-right">Gross Amount</span>
              <span className="w-32 text-right">VAT Amount</span>
              <span className="w-32 text-right">Net Amount</span>
            </div>

            {/* VAT Records Rows */}
            {vatData && vatData.length > 0 ? (
              <div className="space-y-0 mb-6">
                {vatData.map((item, idx) => (
                  <div key={`vat-${item._id || idx}`} className="flex justify-between py-2 text-gray-900 border-b border-gray-200">
                    <span className="flex-1">{monthNames[item.month] || item.month}</span>
                    <span className="flex-1">{recordTypeLabels[item.recordType] || item.recordType}</span>
                    <span className="w-32 text-right font-mono">
                      {formatCurrency(item.grossAmount)}
                    </span>
                    <span className="w-32 text-right font-mono text-green-600">
                      {formatCurrency(item.vatAmount)}
                    </span>
                    <span className="w-32 text-right font-mono">
                      {formatCurrency(item.netAmount)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center text-gray-500">
                No VAT records found for this period
              </div>
            )}

            {/* Totals Section */}
            {vatData && vatData.length > 0 && (
              <div className="border-t-2 border-gray-900 pt-4 space-y-2">
                <div className="flex justify-between font-bold text-gray-900">
                  <span className="flex-1">TOTALS</span>
                  <span className="flex-1"></span>
                  <span className="w-32 text-right border-b border-gray-900">
                    {formatCurrency(totals.totalGross)}
                  </span>
                  <span className="w-32 text-right border-b border-gray-900 text-green-600">
                    {formatCurrency(totals.totalVAT)}
                  </span>
                  <span className="w-32 text-right border-b border-gray-900">
                    {formatCurrency(totals.totalNet)}
                  </span>
                </div>
              </div>
            )}

          </div>
        )}

        {!loading && (!vatData || vatData.length === 0) && !error && (
          <div className="text-center text-gray-500 py-12">
            Select a fiscal year and quarter to view VAT records
          </div>
        )}
      </div>
    </div>
  );
};

export default VATReport;
